import { zodResolver } from '@hookform/resolvers/zod';
import { useContext, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { toast } from 'react-toastify';
import * as z from 'zod';
import { LoadingIcon } from '../../../assets/Icon';
import useAPI from '../../../hooks/useAPI';
import useLoginUserData from '../../../hooks/useLoginUserData';
import Modal from '../../common/modal/Modal';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import TransactionSuccessModal from '../transaction-success-dialog';
import styles from './style.module.scss';
import WalletContext from '../../../contexts/WalletContext';
import TokenListContext from '../../../contexts/TokenListContext';

type RequestCashOutModalProps = {
  open: boolean;
  onClose: () => void;
  refetch?: () => void;
};

const schema = z.object({
  amount: z
    .string()
    .min(1, 'Total tokens must be greater than 0')
    .refine(val => Number(val) !== 0, {
      message: 'Enter a valid number of tokens',
    }),
});

const RequestCashOutModal = ({
  onClose,
  open,
  refetch: dataRefetch,
}: RequestCashOutModalProps) => {
  const [cashOut, setCashOut] = useState(false);
  const { data: userData } = useLoginUserData();
  const { refetchTransactions, refetch: walletBalanceRefetch } =
    useContext(WalletContext);
  const { tokenList } = useContext(TokenListContext);
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'all',
  });

  const { refetch, isLoading, data } = useAPI({
    url: '/withdrawrequest/create',
    method: 'POST',
    onSuccess: () => {
      onClose();
      dataRefetch && dataRefetch();
      refetchTransactions();
      setCashOut(true);
      reset();
      walletBalanceRefetch();
    },
    onError: error => {
      toast.error(error?.response?.data?.message || 'An error occurred');
      reset();
      onClose();
    },
  });

  const onSubmit = (data: FieldValues) => {
    if (tokenList[0]?.tokenId) {
      const payload = {
        amount: data?.amount,
        address: userData?.wallet[0]?.walletAddress || '',
      };
      if (data?.amount > 0) {
        refetch(payload);
      } else {
        toast.error('Insufficient Balance');
      }
    } else {
      toast.error(
        'Your token request cannot be processed as no tokens have been created yet. Please contact your administrator to generate the necessary tokens...',
      );
    }
  };

  return (
    <>
      <TransactionSuccessModal
        open={cashOut}
        title="Requested Cash out to the Admin successfully!"
        isCloseIconVisible={true}
        onClose={() => setCashOut(false)}
      />
      <Modal
        open={open}
        title="Request Cash Out"
        onClose={() => {
          onClose();
          reset();
        }}
        isCloseIconVisible={!isLoading}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group className="mb-3">
            <Form.Label htmlFor="" className={styles.label}>
              Enter Amount <span className="text-danger">*</span>
            </Form.Label>
            <Controller
              name="amount"
              control={control}
              render={({ field }) => (
                <div style={{ position: 'relative' }}>
                  <NumericFormat
                    thousandSeparator
                    decimalScale={0}
                    allowNegative={false}
                    customInput={Input}
                    onValueChange={value => field.onChange(value.value)}
                    error={errors.amount?.message as string}
                    style={{
                      paddingRight: tokenList?.[0]?.tokenSymbol
                        ? '60px'
                        : '10px',
                    }}
                  />
                  {tokenList?.[0]?.tokenSymbol && (
                    <span
                      style={{
                        position: 'absolute',
                        right: '15px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#666',
                        fontWeight: 600,
                        pointerEvents: 'none',
                      }}
                    >
                      {tokenList[0].tokenSymbol}
                    </span>
                  )}
                </div>
              )}
            />
          </Form.Group>
          {/* <div className={styles.information}>
            Amount <span>{watch('amount') || 0}</span> will be added to your
            wallet from your linked account No.{' '}
            {transformString(userData?.wallet[0]?.walletAddress)}
          </div> */}
          <div className={styles.actions}>
            <Button
              variant="outline"
              type="button"
              disabled={isLoading}
              onClick={() => {
                onClose();
                reset();
              }}
            >
              Cancel
            </Button>
            <Button disabled={isLoading} variant="dark" type="submit">
              {isLoading && <LoadingIcon size={16} />}
              Request
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default RequestCashOutModal;
